import { useState } from 'react';
import { SectionHeader, Button } from '../../components/Elements';
import { MiniSwitch } from '../../components/MiniSwitch.jsx';
import { useSection } from "./useSection.js";
import { useSessionCtx } from "../../context/SessionContext";
import { publishReloadToken, updateDetailFlags } from "../../hooks/adminStore.js";
import { DETAIL_TOGGLES } from "../../constants/constants.js";

function ConfigSkeleton() {
  return (
    <div className="w-full max-w-5xl animate-pulse">
      <div className="h-8 bg-gray-200 rounded-xl w-64 mb-6" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="rounded-xl border border-gray-200 p-3 bg-white">
            <div className="h-4 bg-gray-200 rounded w-24 mb-3 mx-2" />
            {[1, 2, 3, 4].map((j) => (
              <div key={j} className="flex items-center justify-between py-1.5 px-2">
                <div className="h-3 bg-gray-100 rounded w-20" />
                <div className="h-4 w-7 bg-gray-200 rounded-full" />
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="h-10 bg-gray-200 rounded-xl w-48" />
    </div>
  );
}

// Single-switch sections shown together in one card (display only: each switch still
// toggles its own section in the config, so the saved format is unchanged).
const COMBINED_SECTION_KEYS = ["banner", "videoPlayer", "speakers", "footer"];

// SECTIONS -> cards to render. The combined card sits where its first member was.
function buildDisplayGroups(sections) {
  const groups = [];
  let combined = null;
  for (const s of sections) {
    if (COMBINED_SECTION_KEYS.includes(s.key)) {
      if (!combined) {
        combined = { key: "pageSections", title: "Page Sections", items: [] };
        groups.push(combined);
      }
      // Label with the section name ("Video Player"), not its field ("Player Visible").
      s.fields.forEach((f) => combined.items.push({ section: s, field: f, label: s.title }));
    } else {
      groups.push({ key: s.key, title: s.title, items: s.fields.map((f) => ({ section: s, field: f, label: f.label })) });
    }
  }
  return groups;
}

// embedded: hides this page's own SectionHeader (for use inside another container).
export default function Sectoin({ embedded = false } = {}) {
  const { compConfig, configLoading, handleCompToggle, resetComponents, getVal, totalEnabled, totalFields, SECTIONS } = useSection();
  const ctx = useSessionCtx();
  const [reloading, setReloading] = useState(false);

  // Publishes a fresh reloadToken on cme-common; every open viewer tab reloads once.
  const reloadUsers = async () => {
    if (reloading) return;
    const id = ctx?.session?.sessionId;
    if (!id) {
      ctx?.showMsg?.("No session - cannot reload viewers", "error");
      return;
    }
    if (!window.confirm("Reload the page for all connected viewers now?")) return;
    setReloading(true);
    try {
      await publishReloadToken(id);
      ctx?.showMsg?.("Reload sent to viewers");
    } catch (e) {
      console.warn("[SectionPage] reload publish failed:", e?.message || e);
      ctx?.showMsg?.("Failed to send reload to viewers", "error");
    } finally {
      setReloading(false);
    }
  };

  // Like / Share: cme-common detail flags, not `setting` - shown as viewers see them (a
  // missing flag hides the button there too) and published as a detail update.
  const detail = ctx?.session?.data?.detail;
  const toggleDetailFlag = (key) => {
    const id = ctx?.session?.sessionId;
    if (!id) {
      ctx?.showMsg?.("No session - cannot update viewers", "error");
      return;
    }
    Promise.resolve()
      .then(() => updateDetailFlags(id, { [key]: !detail?.[key] }))
      .catch((e) => {
        console.warn("[SectionPage] like/share publish failed:", e?.message || e);
        ctx?.showMsg?.("Failed to update viewers", "error");
      });
  };

  if (configLoading || !compConfig) return <ConfigSkeleton />;

  // Every card as { key, title, items: [{ id, label, checked, onToggle }] }. The Like / Share
  // switches sit at the end of the Session Info card.
  const detailItems = DETAIL_TOGGLES.map((f) => ({
    id: `detail-${f.key}`,
    label: f.label,
    checked: !!detail?.[f.key],
    onToggle: () => toggleDetailFlag(f.key),
  }));
  const cards = buildDisplayGroups(SECTIONS).map((group) => ({
    key: group.key,
    title: group.title,
    items: [
      ...group.items.map(({ section, field: f, label }) => ({
        id: `${section.key}-${f.key}`,
        label,
        checked: !!getVal(section, f),
        onToggle: () => handleCompToggle(section, f.key),
      })),
      ...(group.key === "subNavbar" ? detailItems : []),
    ],
  }));

  return (
    <div className="w-full max-w-5xl">
      {!embedded && (
      <SectionHeader
        title="Component Visibility"
        icon="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
        description="Control which sections are visible on the session page"
        badge={
          <div className="px-3 py-2 sm:px-4 bg-gray-100 text-gray-900 rounded-xl border border-gray-200">
            <span className="text-xl sm:text-2xl font-bold">{totalEnabled}</span>
            <span className="text-gray-500">/ {totalFields}</span>
            <span className="text-xs sm:text-sm ml-1">active</span>
          </div>
        }
      />
      )}

      {/* Compact cards: up to 4 per row, small padding, x/y count only, small switches. */}
      {/* Embedded (inside the header Settings modal): at most two columns. */}
      <div className={`grid gap-3 mb-4 ${embedded ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"}`}>
        {cards.map((group) => {
          const on = group.items.filter((it) => it.checked).length;
          const total = group.items.length;
          const allOn = on === total;
          return (
            <div key={group.key} className="bg-white rounded-xl border border-gray-200 shadow-sm p-3">
              <div className="flex items-center justify-between mb-1.5 px-2">
                <h3 className="text-sm font-semibold text-gray-800 truncate">{group.title}</h3>
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${allOn ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                  {on}/{total}
                </span>
              </div>
              <div>
                {group.items.map((it) => (
                  <MiniSwitch
                    key={it.id}
                    block
                    size="xs"
                    tone="dark"
                    label={it.label}
                    checked={it.checked}
                    onChange={it.onToggle}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2 p-3 bg-white rounded-xl border border-gray-200 shadow-sm">
        <Button onClick={resetComponents} variant="danger" size="sm">
          Reset to Defaults
        </Button>
        <Button onClick={reloadUsers} variant="secondary" size="sm" disabled={reloading}>
          {reloading ? 'Sending...' : 'Reload User Page'}
        </Button>
      </div>
    </div>
  );
}
